import { FC, useMemo } from 'react';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Button,
  InfiniteList,
  TableToolbar,
  TableWrapper,
  Tabs,
} from 'melp-design/components';
import { useConfirm } from 'store/confirm';
import { useSearchParams } from 'utils/navigation';
import {
  shopItemsByBenefitFiltersSchema,
  shopItemsByBenefitListVariants,
  taxExemptionStatuses,
  useInfiniteShopItemsByBenefit,
} from 'store/shop';
import {
  benefitCategories,
  BenefitCategory,
  useAssignShopItems,
  useBenefitShopItemsConfig,
  useUnassignShopItems,
} from 'store/benefits';
import {
  FilterButton,
  Filters,
  SelectFilter,
  ShopItemsFilter,
  SuppliersFilter,
} from 'components/filters';
import CurrencyRangeFilter from 'components/filters/CurrencyRangeFilter';
import { APP } from 'config';
import { formatCountry } from 'utils/format';
import { shopItemCategories } from 'types/MarketplaceItems';
import { useMe } from 'state/Administrators';
import { useModalContext } from 'store/modal';
import ItemCard from './ItemCard';
import { BenefitShopItemsConfigModal } from './atoms';

interface Props {
  benefitId: string;
  benefitCategory: BenefitCategory;
}

export const MarketplaceWithinBenefit: FC<Props> = ({
  benefitId,
  benefitCategory,
}) => {
  const { t, i18n } = useTranslation();
  const { taxExemptionCountries, canEditBenefits, hasEditPermission } = useMe();
  const { confirmation } = useConfirm();
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    shopItemsByBenefitFiltersSchema,
  );

  const { data: config } = useBenefitShopItemsConfig(benefitId);
  const { data, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteShopItemsByBenefit(benefitId, {
      ...filters,
      ...(filters.listVariant === 'recommended'
        ? { benefitCategories: [benefitCategory] }
        : filters.listVariant === 'selected'
        ? { isSelected: true }
        : {}),
      taxExemptionCountries,
    });

  const shopItems = useMemo(
    () => ({
      items: (data?.pages.map(({ items }) => items) ?? []).flat(),
      total: data?.pages[0]?.total ?? 0,
    }),
    [data],
  );

  const { mutate: assignShopItems, isLoading: isAssigning } =
    useAssignShopItems();
  const { mutate: unassignShopItems, isLoading: isUnassigning } =
    useUnassignShopItems();

  const { listVariant, ...baseFilters } = filters;

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Tabs
              value={listVariant}
              items={shopItemsByBenefitListVariants.map((v) => ({
                value: v,
                label: t(`marketplace.${v}`),
              }))}
              onChange={(v) => navigate({ params: { listVariant: v } })}
            />

            {config !== undefined && (
              <Button
                label={t('recognitions.rewards.actions.set_config')}
                variant="tertiary"
                size="sm"
                disabled={!canEditBenefits || !hasEditPermission(['benefits'])}
                onClick={() => {
                  openModal({
                    component: BenefitShopItemsConfigModal,
                    size: 'lg',
                    props: {
                      benefitId,
                      config,
                    },
                  });
                }}
                indicator={!!config}
              />
            )}
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
          >
            <Filters
              value={baseFilters}
              initialValues={shopItemsByBenefitFiltersSchema.parse({
                listVariant,
              })}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
            >
              {({ value: values, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('marketplaceItems.supplier')}
                    value={values.suppliers}
                    initialValue={initialValues.suppliers}
                    onChange={(v) => setFilter('suppliers', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SuppliersFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    name="countries"
                    label={t('benefits.country')}
                    value={values.countries}
                    initialValue={initialValues.countries}
                    onChange={(v) => setFilter('countries', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        multiple
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        options={APP.countries.alpha3.map((alpha3) => ({
                          key: alpha3,
                          label: formatCountry(i18n.language, alpha3),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('common.city.label')}
                    value={values.cities}
                    initialValue={initialValues.cities}
                    onChange={(v) => setFilter('cities', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        multiple
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        options={APP.cities.map((city) => ({
                          key: city,
                          label: city,
                        }))}
                      />
                    )}
                  </FilterButton>

                  {taxExemptionCountries.map((country) => (
                    <FilterButton
                      label={t('taxExemptions.country.label', {
                        country: formatCountry(i18n.language, country),
                      })}
                      value={values.taxExemptionStatus}
                      initialValue={initialValues.taxExemptionStatus}
                      onChange={(v) => setFilter('taxExemptionStatus', v)}
                      key={country}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="taxExemptionStatus"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={taxExemptionStatuses.map((status) => ({
                            key: status,
                            label: t(`taxExemptions.status.${status}`),
                          }))}
                        />
                      )}
                    </FilterButton>
                  ))}

                  {listVariant === 'recommended' ? null : (
                    <FilterButton
                      label={t('marketplace.filtersBenefitCategory')}
                      value={values.benefitCategories}
                      initialValue={initialValues.benefitCategories}
                      onChange={(v) => setFilter('benefitCategories', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          multiple
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={benefitCategories.map((category) => ({
                            key: category,
                            label: t(`menu.${category}`),
                          }))}
                        />
                      )}
                    </FilterButton>
                  )}

                  <FilterButton
                    label={t('marketplaceOrders.itemName')}
                    value={values.items}
                    initialValue={initialValues.items}
                    onChange={(v) => setFilter('items', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <ShopItemsFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('marketplace.filtersItemCategory')}
                    value={values.categories}
                    initialValue={initialValues.categories}
                    onChange={(v) => setFilter('categories', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        multiple
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        options={shopItemCategories.map((category) => ({
                          key: category,
                          label: t(`marketplaceItems.itemCategory-${category}`),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('marketplace.filtersPriceRange')}
                    value={values.price}
                    initialValue={initialValues.price}
                    onChange={(v) => setFilter('price', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <CurrencyRangeFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>
                </>
              )}
            </Filters>

            {shopItems.total ? (
              <>
                <Divider orientation="vertical" variant="middle" flexItem />

                {listVariant === 'all' ? (
                  <Button
                    label={t('marketplace.selectFilteredItems', {
                      count: shopItems.total,
                    })}
                    variant="secondary"
                    size="sm"
                    disabled={
                      isAssigning ||
                      !canEditBenefits ||
                      !hasEditPermission(['benefits'])
                    }
                    onClick={async () => {
                      const { confirmed } = await confirmation(
                        t('marketplace.selectFilteredItemsConfirmation', {
                          count: shopItems.total,
                        }),
                      );

                      if (confirmed) {
                        assignShopItems(
                          { benefitId, filters: baseFilters },
                          {
                            onSuccess: () => {
                              toast.success(
                                t('marketplace.itemsAdditionSuccess', {
                                  count: shopItems.total,
                                }),
                              );
                            },
                          },
                        );
                      }
                    }}
                  />
                ) : listVariant === 'recommended' ? (
                  <Button
                    label={t('marketplace.selectFilteredItems', {
                      count: shopItems.total,
                    })}
                    variant="secondary"
                    size="sm"
                    disabled={
                      isAssigning ||
                      !canEditBenefits ||
                      !hasEditPermission(['benefits'])
                    }
                    onClick={async () => {
                      const { confirmed } = await confirmation(
                        t('marketplace.selectFilteredItemsConfirmation', {
                          count: shopItems.total,
                        }),
                      );

                      if (confirmed) {
                        assignShopItems(
                          {
                            benefitId,
                            filters: {
                              ...baseFilters,
                              benefitCategories: [benefitCategory],
                            },
                          },
                          {
                            onSuccess: () => {
                              toast.success(
                                t('marketplace.itemsAdditionSuccess', {
                                  count: shopItems.total,
                                }),
                              );
                            },
                          },
                        );
                      }
                    }}
                  />
                ) : listVariant === 'selected' ? (
                  <Button
                    label={t('marketplace.removeFilteredItems', {
                      count: shopItems.total,
                    })}
                    variant="danger-outline"
                    size="sm"
                    disabled={
                      isUnassigning ||
                      !canEditBenefits ||
                      !hasEditPermission(['benefits'])
                    }
                    onClick={async () => {
                      const { confirmed } = await confirmation(
                        t('marketplace.removeFilteredItemsConfirmation', {
                          count: shopItems.total,
                        }),
                        undefined,
                        'danger',
                      );

                      if (confirmed) {
                        unassignShopItems(
                          { benefitId, filters: baseFilters },
                          {
                            onSuccess: ({ data: isAutoAssignedItemsExist }) => {
                              if (isAutoAssignedItemsExist) {
                                toast.warning(
                                  t('marketplace.thereAreAutoAssignedItems'),
                                  { autoClose: 10000 },
                                );
                              } else {
                                toast.success(
                                  t('marketplace.itemsRemovalSuccess', {
                                    count: shopItems.total,
                                  }),
                                );
                              }
                            },
                          },
                        );
                      }
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </Stack>
        </Stack>
      </TableToolbar>

      <TableWrapper.InfiniteListWrapper>
        <InfiniteList
          data={shopItems.items}
          renderItem={(item) => (
            <ItemCard
              shopItem={item}
              benefitId={benefitId}
              disabled={
                isAssigning ||
                isUnassigning ||
                !canEditBenefits ||
                !hasEditPermission(['benefits'])
              }
            />
          )}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
        />
      </TableWrapper.InfiniteListWrapper>
    </TableWrapper>
  );
};
